import { Alert, Input, message, Modal, Select, Tabs } from 'antd';
import { BarChart3, Copy, Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { DataTable, type Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/ui/multi-select';
import { authStore } from '@/stores';
import { appConfigAdminStore } from '@/stores/appConfigAdminStore';
import type {
  GatewayPrincipalClientConfigSnippetView,
  GatewayPrincipalClientConfigsView,
  GatewayPrincipalQuota,
  GatewayPrincipalUsageSummaryView,
  GatewayPrincipalViewItem,
} from '@/types';
import {
  buildRestrictionFieldState,
  collectGatewayModelOptions,
  GATEWAY_PROTOCOL_OPTIONS,
  isAllRestrictions,
  serializeRestrictionField,
  type RestrictionFieldState,
} from './accessControl';
import {
  buildCreateFormDefaults,
  buildGatewayPrincipalPreview,
} from './principalDefaults';
import { formatUsageCost, formatUsageInteger } from './usageChartData';
import {
  GatewayUsageCostChart,
  GatewayUsageTokensChart,
} from './usageCharts';

const GATEWAY_PRINCIPALS_DOC_URL =
  'https://ccn53rwonxso.feishu.cn/wiki/Et6awtJC0igqjzk4nmqcLGvrnSf';
const GATEWAY_PRINCIPALS_DOC_TITLE =
  'Nexight 内部使用Codex，Claude code方式（使用三方非官方模型）';

function metadataWithOverrides(
  base: Record<string, unknown>,
  owner: string,
  description: string,
) {
  return {
    ...base,
    owner: owner.trim(),
    description: description.trim(),
  };
}

function metadataString(value: Record<string, unknown>) {
  return JSON.stringify(value || {}, null, 2);
}

function getPrincipalOwner(metadata: Record<string, unknown>) {
  return typeof metadata.owner === 'string' && metadata.owner.trim()
    ? metadata.owner.trim()
    : '—';
}

async function copyConfigContent(content: string, successText: string) {
  try {
    await navigator.clipboard.writeText(content);
    message.success(successText);
  } catch {
    message.error('复制失败，请手动复制');
  }
}

interface PrincipalFormState {
  name: string;
  status: string;
  authMode: string;
  authToken: string;
  generatedUIDSuffix: string;
  allowedProtocols: RestrictionFieldState;
  allowedModels: RestrictionFieldState;
  dailyUsd: string;
  monthlyUsd: string;
  dailyTokens: string;
  owner: string;
  description: string;
  metadata: Record<string, unknown>;
}

function buildQuota(form: PrincipalFormState): GatewayPrincipalQuota {
  return {
    daily_usd: form.dailyUsd.trim(),
    monthly_usd: form.monthlyUsd.trim(),
    daily_tokens: Number(form.dailyTokens || 0),
  };
}

function buildCreateForm(): PrincipalFormState {
  const defaults = buildCreateFormDefaults();
  return {
    name: '',
    status: defaults.status,
    authMode: defaults.authMode,
    authToken: defaults.authToken,
    generatedUIDSuffix: defaults.generatedUIDSuffix,
    allowedProtocols: buildRestrictionFieldState(),
    allowedModels: buildRestrictionFieldState(),
    dailyUsd: defaults.dailyUsd,
    monthlyUsd: defaults.monthlyUsd,
    dailyTokens: defaults.dailyTokens,
    owner: defaults.owner,
    description: defaults.description,
    metadata: defaults.metadata,
  };
}

function buildEditForm(item: GatewayPrincipalViewItem): PrincipalFormState {
  const metadata = item.metadata || {};
  return {
    name: '',
    status: item.status,
    authMode: item.authMode,
    authToken: '',
    generatedUIDSuffix: '',
    allowedProtocols: buildRestrictionFieldState(item.allowedProtocols),
    allowedModels: buildRestrictionFieldState(item.allowedModels),
    dailyUsd: item.quota.daily_usd || '',
    monthlyUsd: item.quota.monthly_usd || '',
    dailyTokens: String(item.quota.daily_tokens || 0),
    owner: typeof metadata.owner === 'string' ? metadata.owner : '',
    description:
      typeof metadata.description === 'string' ? metadata.description : '',
    metadata,
  };
}

function formatDateTime(value: string) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleString('zh-CN');
}

interface RestrictionEditorProps {
  field: RestrictionFieldState;
  label: string;
  allText: string;
  restrictedText: string;
  onChange: (field: RestrictionFieldState) => void;
  options: MultiSelectOption[];
  placeholder: string;
}

const GATEWAY_PRINCIPAL_STATUS_OPTIONS = [
  { label: 'active', value: 'active' },
  { label: 'disabled', value: 'disabled' },
];

function ConfigSnippetCard({
  snippet,
  copyText,
}: {
  snippet: GatewayPrincipalClientConfigSnippetView;
  copyText: string;
}) {
  return (
    <div className="rounded-md border border-border bg-slate-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-medium text-foreground">{snippet.title}</div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">
            {snippet.filePath}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void copyConfigContent(snippet.content, copyText)}
        >
          <Copy className="mr-2 h-4 w-4" />
          复制
        </Button>
      </div>
      <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-slate-950 p-4 font-mono text-xs leading-6 text-slate-50">
        {snippet.content}
      </pre>
    </div>
  );
}

function RestrictionEditor({
  field,
  label,
  allText,
  restrictedText,
  onChange,
  options,
  placeholder,
}: RestrictionEditorProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={field.mode === 'all' ? 'default' : 'outline'}
          onClick={() => onChange({ mode: 'all', selected: [] })}
        >
          {allText}
        </Button>
        <Button
          size="sm"
          variant={field.mode === 'restricted' ? 'default' : 'outline'}
          onClick={() =>
            onChange({
              mode: 'restricted',
              selected: field.selected,
            })
          }
        >
          {restrictedText}
        </Button>
      </div>

      {field.mode === 'all' ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          当前将提交为 `["*"]`，表示全部放开。
        </div>
      ) : (
        <MultiSelect
          options={options}
          selected={field.selected}
          onChange={(selected) =>
            onChange({
              mode: 'restricted',
              selected,
            })
          }
          placeholder={placeholder}
          className="w-full"
        />
      )}
    </div>
  );
}

export const AppConfigGatewayPrincipalsPage = observer(() => {
  const canRead = authStore.can(ADMIN_PERMISSIONS.gatewayPrincipalsRead);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.gatewayPrincipalsWrite);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GatewayPrincipalViewItem | null>(null);
  const [editingItem, setEditingItem] = useState<GatewayPrincipalViewItem | null>(null);
  const [configTarget, setConfigTarget] = useState<GatewayPrincipalViewItem | null>(null);
  const [configView, setConfigView] = useState<GatewayPrincipalClientConfigsView | null>(null);
  const [usageTarget, setUsageTarget] = useState<GatewayPrincipalViewItem | null>(null);
  const [usageView, setUsageView] = useState<GatewayPrincipalUsageSummaryView | null>(null);
  const [createForm, setCreateForm] = useState<PrincipalFormState>(() =>
    buildCreateForm(),
  );
  const [editForm, setEditForm] = useState<PrincipalFormState | null>(null);

  useEffect(() => {
    if (canRead) {
      void appConfigAdminStore.fetchGatewayPrincipalsDocument().catch(() => undefined);
    }
  }, [canRead]);

  useEffect(() => {
    if (!configTarget) {
      setConfigView(null);
      return;
    }
    void appConfigAdminStore
      .fetchGatewayPrincipalClientConfigs(configTarget.uid)
      .then((view) => {
        setConfigView(view);
      })
      .catch((error) => {
        message.error(error instanceof Error ? error.message : '加载客户端配置失败');
        setConfigTarget(null);
        setConfigView(null);
      });
  }, [configTarget]);

  useEffect(() => {
    if (!usageTarget) {
      setUsageView(null);
      return;
    }
    void appConfigAdminStore
      .fetchGatewayPrincipalUsageSummary(usageTarget.uid, 30)
      .then((view) => {
        setUsageView(view);
      })
      .catch((error) => {
        message.error(error instanceof Error ? error.message : '加载授权花费失败');
        setUsageTarget(null);
        setUsageView(null);
      });
  }, [usageTarget]);

  const protocolOptions = useMemo<MultiSelectOption[]>(
    () =>
      GATEWAY_PROTOCOL_OPTIONS.map((value) => ({
        label: value,
        value,
      })),
    [],
  );

  useEffect(() => {
    if (!createOpen) {
      setCreateForm(buildCreateForm());
    }
  }, [createOpen]);

  const createPreview = useMemo(
    () =>
      buildGatewayPrincipalPreview(
        createForm.name,
        createForm.generatedUIDSuffix,
        createForm.authToken.trim(),
      ),
    [createForm.authToken, createForm.generatedUIDSuffix, createForm.name],
  );
  const createModelOptions = useMemo<MultiSelectOption[]>(
    () =>
      collectGatewayModelOptions(
        appConfigAdminStore.gatewayDocument,
        createForm.allowedModels.selected,
      ).map((value) => ({
        label: value,
        value,
      })),
    [appConfigAdminStore.gatewayDocument, createForm.allowedModels.selected],
  );
  const editModelOptions = useMemo<MultiSelectOption[]>(
    () =>
      collectGatewayModelOptions(
        appConfigAdminStore.gatewayDocument,
        editForm?.allowedModels.selected || [],
      ).map((value) => ({
        label: value,
        value,
      })),
    [appConfigAdminStore.gatewayDocument, editForm?.allowedModels.selected],
  );

  const columns: Column<GatewayPrincipalViewItem>[] = [
    {
      id: 'label',
      header: 'Label',
      accessor: (row) => <span className="font-mono text-sm">{row.label}</span>,
    },
    {
      id: 'uid',
      header: 'UID',
      accessor: (row) => <span className="font-mono text-sm">{row.uid}</span>,
    },
    {
      id: 'status',
      header: '状态',
      accessor: (row) => row.status || '—',
    },
    {
      id: 'owner',
      header: 'Owner',
      accessor: (row) => getPrincipalOwner(row.metadata),
    },
    {
      id: 'authMode',
      header: '鉴权方式',
      accessor: (row) => row.authMode || '—',
    },
    {
      id: 'authTokenMasked',
      header: 'Token',
      accessor: (row) => (
        <span className="font-mono text-xs">{row.authTokenMasked || '—'}</span>
      ),
    },
    {
      id: 'allowedModels',
      header: '模型数',
      accessor: (row) =>
        isAllRestrictions(row.allowedModels) ? '全部' : row.allowedModels.length,
    },
    {
      id: 'actions',
      header: '操作',
      accessor: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUsageTarget(row);
              setUsageView(null);
            }}
          >
            <BarChart3 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfigTarget(row);
              setConfigView(null);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {canWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingItem(row);
                  setEditForm(buildEditForm(row));
                  setEditOpen(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteTarget(row)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      message.error('名称不能为空');
      return;
    }
    if (!createPreview.uid) {
      message.error('UID 预览生成失败，请检查名称');
      return;
    }
    if (!createForm.authToken.trim()) {
      message.error('Token 不能为空');
      return;
    }
    if (
      createForm.allowedProtocols.mode === 'restricted' &&
      createForm.allowedProtocols.selected.length === 0
    ) {
      message.error('Allowed Protocols 请至少选择一项，或切换为全部放开');
      return;
    }
    if (
      createForm.allowedModels.mode === 'restricted' &&
      createForm.allowedModels.selected.length === 0
    ) {
      message.error('Allowed Models 请至少选择一项，或切换为全部放开');
      return;
    }
    try {
      await appConfigAdminStore.createGatewayPrincipal({
        name: createForm.name.trim(),
        uid: createPreview.uid,
        auth_token: createForm.authToken.trim(),
        status: createForm.status.trim() || undefined,
        auth_mode: createForm.authMode.trim() || undefined,
        allowed_protocols: serializeRestrictionField(createForm.allowedProtocols),
        allowed_models: serializeRestrictionField(createForm.allowedModels),
        quota: buildQuota(createForm),
        metadata: metadataWithOverrides(
          createForm.metadata,
          createForm.owner,
          createForm.description,
        ),
      });
      message.success('内部模型授权已创建');
      setCreateOpen(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建失败');
    }
  };

  const handleUpdate = async () => {
    if (!editingItem || !editForm) {
      return;
    }
    if (
      editForm.allowedProtocols.mode === 'restricted' &&
      editForm.allowedProtocols.selected.length === 0
    ) {
      message.error('Allowed Protocols 请至少选择一项，或切换为全部放开');
      return;
    }
    if (
      editForm.allowedModels.mode === 'restricted' &&
      editForm.allowedModels.selected.length === 0
    ) {
      message.error('Allowed Models 请至少选择一项，或切换为全部放开');
      return;
    }
    try {
      await appConfigAdminStore.updateGatewayPrincipal({
        uid: editingItem.uid,
        auth_token: editForm.authToken.trim() || undefined,
        status: editForm.status.trim() || undefined,
        auth_mode: editForm.authMode.trim() || undefined,
        allowed_protocols: serializeRestrictionField(editForm.allowedProtocols),
        allowed_models: serializeRestrictionField(editForm.allowedModels),
        quota: buildQuota(editForm),
        metadata: metadataWithOverrides(
          editForm.metadata,
          editForm.owner,
          editForm.description,
        ),
      });
      message.success('内部模型授权已更新');
      setEditOpen(false);
      setEditingItem(null);
      setEditForm(null);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '更新失败');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await appConfigAdminStore.deleteGatewayPrincipal(deleteTarget.uid);
      message.success('内部模型授权已删除');
      setDeleteTarget(null);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '删除失败');
    }
  };

  const handleConfigModelChange = async (model: string) => {
    if (!configTarget) {
      return;
    }
    try {
      const nextView = await appConfigAdminStore.fetchGatewayPrincipalClientConfigs(
        configTarget.uid,
        model,
      );
      setConfigView(nextView);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载客户端配置失败');
    }
  };

  const currentVersion = appConfigAdminStore.gatewayDocument?.currentVersion;

  if (!canRead) {
    return (
      <div className="p-6">
        <Alert
          type="warning"
          message="当前账号没有查看内部模型授权的权限"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">内部模型授权</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              管理 `llm_provider_gateway_principals`，创建和维护内部模型授权。
            </p>
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span>使用文档：</span>
              <a
                href={GATEWAY_PRINCIPALS_DOC_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {GATEWAY_PRINCIPALS_DOC_TITLE}
              </a>
            </div>
          </div>
          {canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              新建授权
            </Button>
          ) : null}
        </div>

        {appConfigAdminStore.gatewayError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {appConfigAdminStore.gatewayError}
          </div>
        ) : null}

        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-border bg-slate-50 p-4">
            <div className="text-xs text-muted-foreground">当前 Version</div>
            <div className="mt-1 text-xl font-semibold text-foreground">
              {currentVersion?.versionNumber || '—'}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-slate-50 p-4">
            <div className="text-xs text-muted-foreground">Version Label</div>
            <div className="mt-1 text-sm font-mono text-foreground">
              {currentVersion?.versionLabel || '—'}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-slate-50 p-4">
            <div className="text-xs text-muted-foreground">Deployment</div>
            <div className="mt-1 text-sm text-foreground">
              {currentVersion?.deploymentNumber || '—'} /{' '}
              {currentVersion?.deploymentState || '—'}
            </div>
          </div>
          <div className="rounded-lg border border-border bg-slate-50 p-4">
            <div className="text-xs text-muted-foreground">文档更新时间</div>
            <div className="mt-1 text-sm text-foreground">
              {formatDateTime(appConfigAdminStore.gatewayDocument?.updatedAt || '')}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <DataTable
          columns={columns}
          data={appConfigAdminStore.gatewayDocument?.principals || []}
          loading={appConfigAdminStore.gatewayLoading}
          emptyMessage="暂无内部模型授权"
        />
      </div>

      <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
        <h2 className="text-base font-semibold text-foreground">最近版本</h2>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <DataTable
            columns={[
              {
                id: 'versionNumber',
                header: 'Hosted Version',
                accessor: (row) => row.versionNumber || '—',
              },
              {
                id: 'versionLabel',
                header: 'Label',
                accessor: (row) => row.versionLabel || '—',
              },
            ]}
            data={appConfigAdminStore.gatewayDocument?.recentHostedVersions || []}
            emptyMessage="暂无 hosted version 历史"
          />
          <DataTable
            columns={[
              {
                id: 'deploymentNumber',
                header: 'Deployment',
                accessor: (row) => row.deploymentNumber || '—',
              },
              {
                id: 'deploymentState',
                header: '状态',
                accessor: (row) => row.deploymentState || '—',
              },
              {
                id: 'startedAt',
                header: '开始时间',
                accessor: (row) => formatDateTime(row.startedAt),
              },
            ]}
            data={appConfigAdminStore.gatewayDocument?.recentDeployments || []}
            emptyMessage="暂无 deployment 历史"
          />
        </div>
      </div>

      <Modal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void handleCreate()}
        okText={appConfigAdminStore.gatewaySaving ? '创建中...' : '创建'}
        title="创建内部模型授权"
        width={760}
        confirmLoading={appConfigAdminStore.gatewaySaving}
      >
        <div className="space-y-4 pt-2">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="principal-name">名称 *</Label>
              <Input
                id="principal-name"
                value={createForm.name}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                placeholder="例如：acme team"
              />
              <div className="text-xs text-muted-foreground">
                用于生成 label / uid，建议填写接入方或用途名。
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="principal-status">状态</Label>
              <Select
                value={createForm.status}
                options={GATEWAY_PRINCIPAL_STATUS_OPTIONS}
                onChange={(value) =>
                  setCreateForm((current) => ({
                    ...current,
                    status: value,
                  }))
                }
                className="w-full"
              />
            </div>
          </div>

          <div className="rounded-md border border-border bg-slate-50 p-4 text-sm">
              <div className="font-medium text-foreground">自动生成预览</div>
              <div className="mt-2 space-y-1 font-mono text-xs text-muted-foreground">
                <div>label: {createPreview.label || '—'}</div>
                <div>uid: {createPreview.uid || '—'}</div>
                <div>token: {createPreview.token || '—'}</div>
              </div>
            </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="principal-auth-mode">鉴权方式</Label>
              <Input
                id="principal-auth-mode"
                value={createForm.authMode}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    authMode: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="principal-auth-token">Token（可覆盖默认生成）</Label>
              <Input
                id="principal-auth-token"
                value={createForm.authToken}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    authToken: event.target.value,
                  }))
                }
                placeholder="默认已生成，可直接修改"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="principal-owner">Owner</Label>
              <Input
                id="principal-owner"
                value={createForm.owner}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    owner: event.target.value,
                  }))
                }
              />
              <div className="text-xs text-muted-foreground">
                责任人，便于后续排查和维护。
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="principal-description">Description</Label>
              <Input
                id="principal-description"
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="principal-daily-usd">Daily USD</Label>
              <Input
                id="principal-daily-usd"
                value={createForm.dailyUsd}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    dailyUsd: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="principal-monthly-usd">Monthly USD</Label>
              <Input
                id="principal-monthly-usd"
                value={createForm.monthlyUsd}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    monthlyUsd: event.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="principal-daily-tokens">Daily Tokens</Label>
              <Input
                id="principal-daily-tokens"
                value={createForm.dailyTokens}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    dailyTokens: event.target.value,
                  }))
                }
              />
              <div className="text-xs text-muted-foreground">
                `0` 表示不限制 daily tokens。
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <RestrictionEditor
              label="Allowed Protocols"
              field={createForm.allowedProtocols}
              allText="全部协议"
              restrictedText="限定协议"
              onChange={(allowedProtocols) =>
                setCreateForm((current) => ({
                  ...current,
                  allowedProtocols,
                }))
              }
              options={protocolOptions}
              placeholder="选择允许的协议"
            />
            <RestrictionEditor
              label="Allowed Models"
              field={createForm.allowedModels}
              allText="全部模型"
              restrictedText="限定模型"
              onChange={(allowedModels) =>
                setCreateForm((current) => ({
                  ...current,
                  allowedModels,
                }))
              }
              options={createModelOptions}
              placeholder="选择允许的模型"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditingItem(null);
          setEditForm(null);
        }}
        onOk={() => void handleUpdate()}
        okText={appConfigAdminStore.gatewaySaving ? '保存中...' : '保存'}
        title="编辑内部模型授权"
        width={760}
        confirmLoading={appConfigAdminStore.gatewaySaving}
      >
        {editingItem && editForm ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-md border border-border bg-slate-50 p-4 text-sm">
              <div className="font-medium text-foreground">只读标识</div>
              <div className="mt-2 space-y-1 font-mono text-xs text-muted-foreground">
                <div>label: {editingItem.label}</div>
                <div>uid: {editingItem.uid}</div>
                <div>token: {editingItem.authTokenMasked || '—'}</div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-principal-status">状态</Label>
                <Select
                  value={editForm.status}
                  options={GATEWAY_PRINCIPAL_STATUS_OPTIONS}
                  onChange={(value) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, status: value }
                        : current,
                    )
                  }
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-principal-auth-mode">鉴权方式</Label>
                <Input
                  id="edit-principal-auth-mode"
                  value={editForm.authMode}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, authMode: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-principal-auth-token">新 Token（留空则保留原值）</Label>
              <Input
                id="edit-principal-auth-token"
                value={editForm.authToken}
                onChange={(event) =>
                  setEditForm((current) =>
                    current
                      ? { ...current, authToken: event.target.value }
                      : current,
                  )
                }
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-principal-owner">Owner</Label>
                <Input
                  id="edit-principal-owner"
                  value={editForm.owner}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, owner: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-principal-description">Description</Label>
                <Input
                  id="edit-principal-description"
                  value={editForm.description}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, description: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="edit-daily-usd">Daily USD</Label>
                <Input
                  id="edit-daily-usd"
                  value={editForm.dailyUsd}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, dailyUsd: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-monthly-usd">Monthly USD</Label>
                <Input
                  id="edit-monthly-usd"
                  value={editForm.monthlyUsd}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, monthlyUsd: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-daily-tokens">Daily Tokens</Label>
                <Input
                  id="edit-daily-tokens"
                  value={editForm.dailyTokens}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current
                        ? { ...current, dailyTokens: event.target.value }
                        : current,
                    )
                  }
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <RestrictionEditor
                label="Allowed Protocols"
                field={editForm.allowedProtocols}
                allText="全部协议"
                restrictedText="限定协议"
                onChange={(allowedProtocols) =>
                  setEditForm((current) =>
                    current ? { ...current, allowedProtocols } : current,
                  )
                }
                options={protocolOptions}
                placeholder="选择允许的协议"
              />
              <RestrictionEditor
                label="Allowed Models"
                field={editForm.allowedModels}
                allText="全部模型"
                restrictedText="限定模型"
                onChange={(allowedModels) =>
                  setEditForm((current) =>
                    current ? { ...current, allowedModels } : current,
                  )
                }
                options={editModelOptions}
                placeholder="选择允许的模型"
              />
            </div>

            <div className="rounded-md border border-border bg-slate-50 p-4 text-xs text-muted-foreground">
              <div>Metadata Preview</div>
              <pre className="mt-2 whitespace-pre-wrap break-all">
                {metadataString(
                  metadataWithOverrides(
                    editForm.metadata,
                    editForm.owner,
                    editForm.description,
                  ),
                )}
              </pre>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={!!configTarget}
        onCancel={() => {
          setConfigTarget(null);
          setConfigView(null);
        }}
        footer={null}
        title={configTarget ? `客户端配置 · ${configTarget.label}` : '客户端配置'}
        width={880}
      >
        {appConfigAdminStore.gatewayConfigLoading && !configView ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            正在加载客户端配置...
          </div>
        ) : configView ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="font-medium text-foreground">按文档拆分的可复制配置</div>
                  <div className="mt-1 font-mono text-xs text-muted-foreground">
                    uid: {configView.uid}
                  </div>
                </div>
                {configView.availableModels.length > 1 ? (
                  <div className="w-full md:w-72">
                    <Label htmlFor="client-config-model">模型</Label>
                    <Select
                      id="client-config-model"
                      className="mt-2 w-full"
                      value={configView.selectedModel || undefined}
                      options={configView.availableModels.map((value) => ({
                        label: value,
                        value,
                      }))}
                      onChange={(value) => void handleConfigModelChange(value)}
                    />
                  </div>
                ) : null}
              </div>
              {configView.selectedModel ? (
                <div className="mt-3 text-xs text-muted-foreground">
                  当前配置使用模型 `{configView.selectedModel}`。
                </div>
              ) : (
                <div className="mt-3 text-xs text-amber-700">
                  当前授权未锁定单个模型，配置中的 `&lt;your-model&gt;` 需要按需替换。
                </div>
              )}
            </div>

            <Tabs
              items={[
                {
                  key: 'codex',
                  label: 'Codex 配置',
                  children:
                    configView.codexSnippets.length > 0 ? (
                      <div className="space-y-4">
                        {configView.codexSnippets.map((snippet) => (
                          <ConfigSnippetCard
                            key={snippet.id}
                            snippet={snippet}
                            copyText={`${snippet.title} 已复制`}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-muted-foreground">
                        当前授权未开放 Codex 所需的 `openai_responses` 协议。
                      </div>
                    ),
                },
                {
                  key: 'claude-code',
                  label: 'Claude Code 配置',
                  children:
                    configView.claudeCodeSnippets.length > 0 ? (
                      <div className="space-y-4">
                        {configView.claudeCodeSnippets.map((snippet) => (
                          <ConfigSnippetCard
                            key={snippet.id}
                            snippet={snippet}
                            copyText={`${snippet.title} 已复制`}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-muted-foreground">
                        当前授权未开放 Claude Code 所需的 `anthropic_messages` 协议。
                      </div>
                    ),
                },
              ]}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={!!usageTarget}
        onCancel={() => {
          setUsageTarget(null);
          setUsageView(null);
        }}
        footer={null}
        title={usageTarget ? `最近 30 天花费 · ${usageTarget.label}` : '最近 30 天花费'}
        width={1080}
      >
        {appConfigAdminStore.gatewayUsageLoading && !usageView ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            正在加载授权花费...
          </div>
        ) : usageView ? (
          <div className="space-y-4 pt-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="font-medium text-foreground">按 key / principal 聚合的最近 30 天账本</div>
                  <div className="mt-1 font-mono text-xs text-muted-foreground">
                    uid: {usageView.uid}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  最近记录时间：{formatDateTime(usageView.lastRecordedAt)}
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border border-border bg-white p-4">
                <div className="text-xs text-muted-foreground">30 天总花费</div>
                <div className="mt-1 text-xl font-semibold text-foreground">
                  {formatUsageCost(usageView.totalCostUsd)}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-white p-4">
                <div className="text-xs text-muted-foreground">30 天总 Tokens</div>
                <div className="mt-1 text-xl font-semibold text-foreground">
                  {formatUsageInteger(usageView.totalTokens)}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-white p-4">
                <div className="text-xs text-muted-foreground">请求数</div>
                <div className="mt-1 text-xl font-semibold text-foreground">
                  {formatUsageInteger(usageView.requestCount)}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-white p-4">
                <div className="text-xs text-muted-foreground">缓存读 / 写 Tokens</div>
                <div className="mt-1 text-sm font-semibold text-foreground">
                  {formatUsageInteger(usageView.cacheReadTokens)} /{' '}
                  {formatUsageInteger(usageView.cacheWriteTokens)}
                </div>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <GatewayUsageCostChart summary={usageView} />
              <GatewayUsageTokensChart summary={usageView} />
            </div>

            <div className="rounded-lg border border-border bg-white p-4">
              <div className="mb-4 text-sm font-medium text-foreground">模型使用明细</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">模型</th>
                      <th className="px-3 py-2 font-medium">Upstream</th>
                      <th className="px-3 py-2 font-medium">请求数</th>
                      <th className="px-3 py-2 font-medium">总 Tokens</th>
                      <th className="px-3 py-2 font-medium">Input</th>
                      <th className="px-3 py-2 font-medium">Output</th>
                      <th className="px-3 py-2 font-medium">Cache Read</th>
                      <th className="px-3 py-2 font-medium">Cache Write</th>
                      <th className="px-3 py-2 font-medium">花费</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageView.models.map((item) => (
                      <tr key={item.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs text-foreground">
                          {item.publicModel || 'unknown'}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                          {item.upstreamModel || '—'}
                        </td>
                        <td className="px-3 py-2">{formatUsageInteger(item.requestCount)}</td>
                        <td className="px-3 py-2">{formatUsageInteger(item.totalTokens)}</td>
                        <td className="px-3 py-2">{formatUsageInteger(item.inputTokens)}</td>
                        <td className="px-3 py-2">{formatUsageInteger(item.outputTokens)}</td>
                        <td className="px-3 py-2">{formatUsageInteger(item.cacheReadTokens)}</td>
                        <td className="px-3 py-2">{formatUsageInteger(item.cacheWriteTokens)}</td>
                        <td className="px-3 py-2 font-medium">{formatUsageCost(item.costUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {usageView.models.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  最近 30 天暂无该授权的模型使用记录。
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={() => void handleDelete()}
        okText={appConfigAdminStore.gatewaySaving ? '删除中...' : '确认删除'}
        okButtonProps={{ danger: true }}
        title="删除内部模型授权"
        confirmLoading={appConfigAdminStore.gatewaySaving}
      >
        <p className="text-sm text-muted-foreground">
          确认删除 `{deleteTarget?.label}` 吗？该操作会发布新的 AppConfig hosted version。
        </p>
      </Modal>
    </div>
  );
});
